/* eslint-disable @typescript-eslint/indent */
/* eslint-disable indent */
import React from 'react'
import {useSelector} from 'react-redux'
import PropTypes from 'prop-types'
import {Box, CircularProgress, Typography} from '@mui/material'
import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp'

import PasswordField from 'shared/PasswordField'
import FormikMultiSelect from 'shared/FormikMultiSelect'
import FormikSelect from 'shared/FormikSelect'
import FormikAutocomplete from 'shared/FormikAutoComplete'
import ReactCountryFlag from 'react-country-flag'
import FormikPhone from 'shared/FormikPhone'
import FormikTextField from 'shared/FormikTextField'
import InfoTooltip from 'shared/InfoTooltip'

interface InputProps {
    id: string
    label: string
    type?: string
    placeholder?: string
    formik: any
    helperText?: string
    typeHTML?: string
    flagId?: string
    error?: boolean
    errorMsg?: string
    reflectField?: string
    showAstrik?: boolean
    autoFocus?: boolean
    showStrength?: boolean
    showInputInfo?: any
    inputInfoVisible?: boolean
}

const Input = ({
    id,
    placeholder,
    label,
    formik,
    type,
    reflectField,
    typeHTML,
    showAstrik,
    flagId,
    helperText,
    error,
    errorMsg,
    autoFocus,
    showStrength = true,
    showInputInfo,
    inputInfoVisible = false
}: InputProps) => {
    const allCarriers = useSelector((state: any) => state.common.allCarriers)
    const branchDetails = useSelector((state: any) => state.common.branchDetails)
    const isLoading = useSelector((state: any) => state.common.isLoading)
    const list: string[] = ['description', 'branchNumber', 'zip']
    const isDisabledId = 'country'

    const textFieldHandler = (event) => {
        if (id === 'number' && formik.values.accountType === 'IATA Account') {
            let cleanedInput = event.target.value.replace(/\D/g, '')
            let formattedNumber = ''
            if (cleanedInput.length > 11) {
                cleanedInput = cleanedInput.substring(0, 11)
            }

            if (cleanedInput.length > 7) {
                // eslint-disable-next-line max-len
                formattedNumber = `${cleanedInput.slice(0, 2)}-${cleanedInput.slice(2, 3)} ${cleanedInput.slice(
                    3,
                    7
                )}/${cleanedInput.slice(7)}`
            } else if (cleanedInput.length > 3) {
                formattedNumber = `${cleanedInput.slice(0, 2)}-${cleanedInput.slice(2, 3)} ${cleanedInput.slice(3)}`
            } else if (cleanedInput.length > 2) {
                formattedNumber = `${cleanedInput.slice(0, 2)}-${cleanedInput.slice(2, 3)}`
            } else {
                formattedNumber = cleanedInput
            }
            formik.setFieldValue(id, formattedNumber)
        }
    }

    const getInput = (type: string) => {
        switch (type?.toLowerCase() || 'textfield') {
            case 'textfield':
                return (
                    <FormikTextField
                        id={id}
                        formik={formik}
                        className={
                            list.includes(id)
                                ? 'formik-input__textfield formik-input__special'
                                : id === 'country'
                                ? 'formik-input__textfield formik-input__textfield-disabled'
                                : 'formik-input__textfield'
                        }
                        placeholder={placeholder}
                        typeHTML={typeHTML}
                        disabled={isDisabledId === id}
                        error={error}
                        errorMsg={errorMsg}
                        onChange={textFieldHandler}
                        autoFocus={autoFocus}
                    />
                )
            case 'password':
                return (
                    <PasswordField
                        id={id}
                        name={id}
                        setFieldTouched={formik.setFieldTouched}
                        value={formik.values[id]}
                        onChange={formik.handleChange}
                        error={formik.touched[id] && Boolean(formik.errors[id])}
                        helperText={formik.touched[id] && formik.errors[id]}
                        className='formik-input__password'
                        formik={formik}
                        showStrength={showStrength}
                    />
                )
            case 'autocomplete':
                return (
                    <FormikAutocomplete
                        className='formik-input__autocomplete'
                        id={id}
                        reflectField={reflectField}
                        formik={formik}
                        renderOption={(p, o) => (
                            <Box
                                component='li'
                                {...p}
                                key={`${o.code}: ${o.airportName ? o.airportName : o.city}`}
                                className='formik_autocomplete__items'
                            >
                                <Typography variant='h6' key={o.code}>
                                    {o.code}:&nbsp;{o.airportName ? o.airportName : o.city}
                                </Typography>
                                <Box className='img-container'>
                                    <ReactCountryFlag countryCode={o.countryCode} svg />
                                </Box>
                            </Box>
                        )}
                        message='Type airport code to view results.'
                    />
                )
            case 'phone':
                return <FormikPhone id={id} flagId={flagId ?? ''} formik={formik} />
            case 'multidropdown':
                return (
                    <FormikMultiSelect
                        id={id}
                        formik={formik}
                        options={typeof allCarriers === 'string' ? [] : allCarriers}
                        className='formik-input__multiselect'
                        error={error}
                        helperText={errorMsg}
                    />
                )
            case 'select':
                return (
                    <FormikSelect
                        id={id}
                        formik={formik}
                        options={[
                            {value: 'Agent', label: 'Agent'},
                            {value: 'Cosignee', label: 'Cosignee'},
                            {value: 'Forwarder', label: 'Forwarder'},
                            {value: 'Shipper', label: 'Shipper'}
                        ]}
                        className='formik-input__select'
                    />
                )
        }
    }

    const getDetails = () => {
        if (id === 'branchNumber') {
            if (!(formik.touched[id] && formik.errors[id])) {
                if (isLoading) {
                    return (
                        <p className='formik__loader'>
                            <CircularProgress />
                        </p>
                    )
                }
                if (branchDetails && branchDetails !== null) {
                    if (branchDetails?.displayName !== null) {
                        return (
                            <p className='formik__success-text'>
                                <CheckCircleSharpIcon fontSize='inherit' />
                                {branchDetails?.displayName}
                            </p>
                        )
                    } else {
                        return <p className='formik___error-text'>Branch Number is invalid</p>
                    }
                } else {
                    return helperText && <p>{helperText}</p>
                }
            }
        } else if (id === 'number' && label === 'IATA Number' && !(formik.touched[id] && Boolean(errorMsg))) {
            return helperText && <p className='iataNumber-font'>{helperText}</p>
        }
    }

    return (
        <div className='input-container'>
            <div className='input-label'>
                <label htmlFor={id}>{label}</label>
                {inputInfoVisible && (
                    <InfoTooltip
                        behaviour='inline'
                        title={showInputInfo?.title}
                        content={showInputInfo?.content}
                        type={showInputInfo?.type}
                        className='input-info-modal'
                        isClosable={showInputInfo?.isClosable}
                    />
                )}
                {showAstrik && <span>&#42;</span>}
            </div>
            {getInput(type ?? 'textfield')}
            {getDetails()}
        </div>
    )
}

Input.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    formik: PropTypes.object.isRequired,
    helperText: PropTypes.string,
    typeHTML: PropTypes.string,
    flagId: PropTypes.string,
    error: PropTypes.bool,
    errorMsg: PropTypes.string,
    reflectField: PropTypes.string,
    showAstrik: PropTypes.bool,
    autoFocus: PropTypes.bool,
    showStrength: PropTypes.bool,
    showInputInfo: PropTypes.any,
    inputInfoVisible: PropTypes.bool
}

Input.defaultProps = {
    type: 'textfield',
    typeHTML: 'text',
    showAstrik: true,
    autoFocus: false,
    showStrength: true,
    showInputInfo: {},
    inputInfoVisible: false
}

export default Input
